import { useSyncExternalStore } from 'react';

import { createExternalStore } from './createExternalStore';

const DEFAULT_BROWSER_WIDTH = 0;
const VIEWPORT_RESIZE_EVENT = 'resize';

/**
 * Reads the current viewport width from the browser.
 *
 * This is the single source of truth for both the render-time read and the
 * resize handler, so the reported value and the update trigger both derive
 * from `window.innerWidth` and can never disagree. Falls back to the default
 * width when no DOM is available.
 */
const readBrowserWidth = (): number => {
  if (typeof window === 'undefined') {
    return DEFAULT_BROWSER_WIDTH;
  }

  return window.innerWidth;
};

/**
 * The shared viewport-width store backing every `useBrowserWidth` consumer.
 *
 * One browser-level `resize` listener serves the entire app instead of one
 * listener per mounted component: the first mounted consumer starts it and
 * the last unmounted consumer removes it. The snapshot is a raw pixel width,
 * so unlike `useIsMobile` every actual width change is published — only
 * no-op resize events are swallowed by the default `Object.is` equality gate.
 * See `createExternalStore` for the full lifecycle contract.
 */
const browserWidthStore = createExternalStore<number>({
  eventName: VIEWPORT_RESIZE_EVENT,
  readSnapshot: readBrowserWidth,
  defaultSnapshot: DEFAULT_BROWSER_WIDTH,
});

/**
 * Provides the live viewport width through one shared external store.
 *
 * Public API is intentionally unchanged: every existing consumer can continue to
 * call `const { browserWidthSize } = useBrowserWidth()`, while React coordinates
 * updates through `useSyncExternalStore` instead of per-component resize state.
 */
const useBrowserWidth = () => {
  const browserWidthSize = useSyncExternalStore(
    browserWidthStore.subscribe,
    browserWidthStore.getSnapshot,
    browserWidthStore.getServerSnapshot,
  );

  return { browserWidthSize };
};

export default useBrowserWidth;
